import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, mergeMap, of, switchMap, withLatestFrom } from 'rxjs';
import { mapReportsToResources } from '../../graphql/functions/mappingFunctions';
import { SchoolLibrary } from '../../models/library';
import { LibraryService } from '../../services/library/library.service';
import { NewsService } from '../../services/resources/news.service';
import { ResourceService } from '../../services/resources/resource.service';
import { StudentService } from '../../services/student/student.service';
import { LibraryActions, ResourceActions } from './resource.actions';
import { selectSelectedSchoolFolders } from './resource.selectors';

@Injectable()
export class ResourceEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly resourceService: ResourceService,
    private readonly studentService: StudentService,
    private readonly newsService: NewsService,
  ) { }

  loadFolders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResourceActions.loadFolders),
      mergeMap(({ schoolCode }) =>
        this.resourceService.getFolders(schoolCode).pipe(
          map((folders) => ResourceActions.loadFoldersSuccess({ schoolCode, folders })),
          catchError((error) => of(ResourceActions.loadFoldersFailure({ error }))),
        ),
      ),
    ),
  );

  selectFolder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResourceActions.selectFolder),
      withLatestFrom(this.store.select(selectSelectedSchoolFolders)),
      map(([{ folderId }, folders]) => {
        const folderType = folders.find((f) => f.id === folderId)?.folderType ?? 'documents';
        return ResourceActions.loadFolderResources({ folderId, folderType });
      }),
    ),
  );

  loadFolderResources$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResourceActions.loadFolderResources),
      mergeMap(({ folderId }) =>
        this.resourceService.getFolderResources(folderId).pipe(
          map((resources) => ResourceActions.loadFolderResourcesSuccess({ folderId, resources })),
          catchError((error) => of(ResourceActions.loadFolderResourcesFailure({ error }))),
        ),
      ),
    ),
  );

  loadStudentReports$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResourceActions.loadStudentReports),
      mergeMap(({ studentId }) => {
        return this.studentService.getReportMetadata(studentId).pipe(
          map((response) => {
            const reports = response?.data?.student?.reports || [];
            const resourceReports = mapReportsToResources(reports);
            return ResourceActions.loadReportsSuccess({ reports: { [studentId]: resourceReports } });
          }),
          catchError((error) => of(ResourceActions.loadReportsFailure({ error }))),
        );
      }),
    ),
  );

  loadNewsArticles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ResourceActions.loadNewsArticles),
      mergeMap(() => {
        return this.newsService.getLatestNews().pipe(
          map((articles) => ResourceActions.loadNewsArticlesSuccess({ articles })),
          catchError((error) => of(ResourceActions.loadNewsArticlesFailure({ error }))),
        );
      }),
    ),
  );
  private readonly libraryService = inject(LibraryService);


  loadLibrary$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LibraryActions.loadSchoolLibrary),

      switchMap(({ schoolCode }) =>
        this.libraryService.getSchoolLibraryData({ schoolCode }).pipe(

          map((response: any): SchoolLibrary => {
            return {
              name: response.Name || response.schoolName || '',
              code: response.Code || response.schoolCode || schoolCode,
              image: response.Image || response.imageUrl || '',
              children: response.Children || response.subFolders || [],
              containers: response.Containers || response.mediaContainers || []
            };
          }),

          map((mappedData: SchoolLibrary) =>
            LibraryActions.hydrateLibraries({ libraries: [mappedData] })
          ),

          catchError((error: HttpErrorResponse) => {
            const statusCode = error.status || 500;
            const errorMessage = statusCode === 404
              ? 'The requested school library profile could not be found.'
              : 'An unexpected connection error occurred. Please try again.';

            return of(LibraryActions.loadSchoolLibraryFailure({
              error: errorMessage,
              statusCode: statusCode
            }));
          })
        )
      )
    )
  );
}
